'use client';
import React, {
    FC,
    useContext,
    useEffect,
    useRef,
    useState,
    useTransition,
} from 'react';
import { useSort, useTranslation, useWindowDimensions } from '@/hook';
import {
    BannerData,
    CarListingData,
    IClientRetailFilters,
    ISearchTerms,
} from '@/types';
import styles from './styles.module.scss';
import {
    AVAILABLE_LANGUAGES,
    ELASTIC_SEARCH_PAGE_SIZE,
    ServiceName,
} from '@/constants';
import {
    buildTrackingObject,
    buildUrl,
    cloneClientRetailFilters,
    convertClientToEsFilters,
    isDefaultFilters,
    isIClientRetailFiltersEqual,
} from '@/utils/filterUtil';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/navigation';
import colors from '@/theme/colors.module.scss';
import Typography from '@/components/shared/typography/typography.component';
import { DimensionsTargets } from '@/hook/use-window-width.hook';
import { getCarListingsElastic } from '@/api-requests/car-listing-elastic.apis';
import { useInView } from 'react-intersection-observer';
import useDebounce from '@/hook/debounce.hook';
import Spinner from '@/components/Spin/Spinner';
import { useAnalytics } from '@/integrations';
import { SORTING_PLANS } from '@/types/filter.types';

import CarDisplayCardClient from '@/components/shared/car-details-card/car-details-car-client.component';
import Sort from '@/components/shared/sort/sort-desktop.component';
import RetailBanners from '@/components/cars-listing/retail-banners/retail-banners.component';
import FinanceCarCard from '@/components/shared/cards_renamed/finance-card/finance-card.component';
import ExchangeCarBanner from '@/components/shared/banners/exchange-car-banner/exchange-car-banner.component';
// import ReserveSeeCarBanner from '@/components/shared/banners/reserve-vs-see-banner/reserve-vs-see-banner.component';
import {
    carPriceDiscountCssClasses,
    carStatusCssClasses,
} from '@/types/cars.types';
import VidTestimonialsSection from '@/components/testimonialVidSection/vid-testimonials.component';
import { SourceScreen } from '@/types/source-screens.types';
import { TFebDiscounts } from '@/api-requests/feb-discounts.apis';
import { getCarCardLocale } from '@/utils';

import FilterHeaderMobile from '@/components/filters/filter-header-mobile/filters-header-mobile.component';
import { globalConfigContext } from '@/wrappers/globalConfig.weapper';
import ValuePropositionDesktop from '@/components/shared/value-proposition-slider/value-proposition-desktop.component';
import ValuePropositionMobile from '@/components/shared/value-proposition-slider/value-proposition-mobile.component';
import { PurchaseCarSteps } from '@/components/purchaseCarSteps';
import { PurchaseCarStepsBanner } from '@/components/purchaseCarSteps/banner';
// const FilterHeaderMobile = dynamic(
//     () =>
//         import(
//             '@/components/filters/filter-header-mobile/filters-header-mobile.component'
//         ).then(mod => mod.default),
//     { ssr: false }
// );
const CantFindCarCard = dynamic(
    () =>
        import(
            '@/components/shared/cards_renamed/can-not-find-car-card/can-not-find-car-card.component'
        ).then(mod => mod.default),
    { ssr: false }
);

const Filters = dynamic(
    () =>
        import('@/components/filters/filter-desktop.component').then(
            mod => mod.default
        ),
    { ssr: false }
);

interface carsListingProps {
    carsData: CarListingData[];
    initFilters: IClientRetailFilters;
    bannersData: BannerData[];
    searchTerms: ISearchTerms;
    lang: AVAILABLE_LANGUAGES;
    listingSize: number;
    febDiscounts: TFebDiscounts;
}

const ScrollToTopButton: FC = () => {
    const [scrollToTopVisible, setScrollToTopVisible] = useState(false);
    useEffect(() => {
        const handleScrollButtonVisability = () => {
            setScrollToTopVisible(window.scrollY > 1200);
        };
        window.addEventListener('scroll', handleScrollButtonVisability);
        return () => {
            window.removeEventListener('scroll', handleScrollButtonVisability);
        };
    }, []);
    return (
        <div
            className={`${
                scrollToTopVisible ? '' : 'hidden'
            }  !fixed  bottom-16 md:bottom-1 end-1 [z-index:100]`}
            onClick={() => {
                window.scrollTo({
                    top: 0,
                    left: 0,
                    behavior: 'auto',
                });
            }}
        >
            <button
                type="button"
                className={` 
               m-4
                rounded-full
                
                bg-gray-100
                hover:bg-gray-500
                p-3 text-xs font-medium uppercase
                leading-tight text-primary-500 shadow-md
                transition duration-150 ease-in-out
                 hover:shadow-lg
                 focus:bg-gray-100
                 focus:shadow-lg
                 focus:outline-none
                 focus:ring-0
                 active:bg-gray-300
                 active:shadow-lg`}
            >
                <span className="[&>svg]:w-4">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth="3"
                        stroke="currentColor"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M4.5 10.5 12 3m0 0 7.5 7.5M12 3v18"
                        />
                    </svg>
                </span>
            </button>
        </div>
    );
};

const CarsListing = ({
    carsData,
    initFilters,
    bannersData,
    searchTerms,
    lang,
    listingSize,
    febDiscounts,
}: carsListingProps) => {
    const { t, IS_RTL } = useTranslation();
    const router = useRouter();

    const resultsCount = useRef(listingSize);
    // const lastItem = useRef(carsData.length);
    const isInitialRender = useRef(true);

    const [hasMoreResults, setHasMoreResults] = useState(true);
    const { ref: loaderRef, inView } = useInView();
    const [isPending, startTransition] = useTransition();
    const [pagePending, setPagePending] = useState(false);
    const filters = useRef<IClientRetailFilters>(initFilters);
    const prevFilters = useRef<IClientRetailFilters>({
        ...initFilters,
    });
    // TODO:: DELETE AFTER EXP END SEARCH IN PROJECT FOR CODE SORTING_EXP_X1
    //used to trigger sorting for mobile devices
    //@ts-ignore
    const { sorting_plan: sortingOptions } = useSort();
    const prevSortValue = useRef(SORTING_PLANS.MOST_RELEVANT);
    // TODO:: DELETE AFTER EXP END SEARCH IN PROJECT FOR CODE SORTING_EXP_X1
    // const { sorting_plan: sortingOptions } = useAppSelector(
    //     state => state.sort
    // );

    const setFilters = (newFilters: IClientRetailFilters) => {
        if (!isIClientRetailFiltersEqual(newFilters, prevFilters.current)) {
            filters.current = newFilters;
            doSearch(true);
        }
    };
    const { trackEvent } = useAnalytics();

    // const dispatch = useDispatch();
    const carListingFlagInPartialLoading = useContext(globalConfigContext);
    useEffect(() => {
        if (hasMoreResults != carListingFlagInPartialLoading.hideFooter) {
            carListingFlagInPartialLoading.setConfig({
                hideFooter: hasMoreResults,
            });
        }
        return () => {
            carListingFlagInPartialLoading.setConfig({
                hideFooter: false,
            });
        };
    }, [hasMoreResults]);
    useEffect(() => {
        if (!isInitialRender.current) return;
        // dispatch(setSortingOption(SORTING_PLANS.DEFAULT));
        trackEvent('Screen: Cars Listing', {
            'Results Found': resultsCount.current,
            'Used Sorting': sortingOptions,
            ...buildTrackingObject(filters.current, resultsCount.current),
        });
        isInitialRender.current = false;
        return () => {
            // filters.current = getDefaultIClientRetailFilters();
            router.refresh();
        };
    }, []);
    useEffect(() => {
        if (prevSortValue.current != sortingOptions) {
            prevSortValue.current = sortingOptions;
            doSearch();
        }
    }, [sortingOptions]);
 
    const exchangePositionTablet = 10;
    const exchangePositionDesktop = 11;

    const valuePropositionDesktop = 8;
    const valuePropositionTablet = 8;
    const valuePropositionMobile = 9;

    const PurchaseCarStepsPositionMobile = 1;

    const reserveSeeDesktop =
        resultsCount.current < 6 ? resultsCount.current - 1 : 5;
    const financePositionAll =
        resultsCount.current < 6 ? resultsCount.current - 1 : 12;
    const isFilterApplied = !isDefaultFilters(filters.current);
    const isSortApplied =
        sortingOptions && sortingOptions != SORTING_PLANS.MOST_RELEVANT;

    interface renderCardsProps {
        item: CarListingData;
        carIndexUsed: number;
    }

    const carCardLocalizations = getCarCardLocale();
    const RenderCards = ({ item, carIndexUsed }: renderCardsProps) => {
        const renderKey = `car-${item.id}-${item.status}-${item.updatedAt}-${
            item.isNewlyAdded ? 'new' : 'old'
        }`;
        return (
            <>
                <div
                    className={` flex-1  ${carStatusCssClasses[item.status]} ${
                        item.discountPrice === 0
                            ? carPriceDiscountCssClasses.NO_DISCOUNT
                            : carPriceDiscountCssClasses.HAS_DISCOUNT
                    }`}
                    id={`cardofcar-${item.id}`}
                    key={renderKey}
                    data-key={renderKey}
                >
                    <CarDisplayCardClient
                        carCardLocalizations={carCardLocalizations}
                        lang={lang}
                        useNextImage={carIndexUsed === 0}
                        id={item.id}
                        febDiscount={febDiscounts[item.id] || 0}
                        cardTitle={`${t(item.make)} ${item.model} ${item.year}`}
                        carName={item.sfVehicleName}
                        trim={item.trim}
                        installmentPrice={item?.installmentPrice}
                        totalPrice={item.sylndrSellingPrice}
                        kilometers={item.kilometers}
                        year={item.year}
                        transmission={item.transmission}
                        isPurchaseInProgress={item.isPurchaseInProgress}
                        images={item?.images}
                        isReserved={item.isReserved}
                        make={item.make}
                        model={item.model}
                        discountPrice={item.discountPrice}
                        isNewlyAdded={item.isNewlyAdded}
                        sourceScreen={'Cars Listing'}
                        appliedFilter={isFilterApplied}
                        appliedSort={isSortApplied}
                        position={carIndexUsed}
                    />
                </div>

                {
                carIndexUsed <= 29 && (carIndexUsed === PurchaseCarStepsPositionMobile || resultsCount.current == 1) && (
                    <div
                        key={`ReserveSeeCarBanner-${carIndexUsed}-banner`}
                        className={'md:hidden flex-1'}
                    >
                        <PurchaseCarSteps
                            key={`ReserveSeeCarBanner-${carIndexUsed}-banner-steps`}
                        />
                    </div>
                )
                }
                {carIndexUsed === exchangePositionTablet && (
                    <div
                        key={`exbannerpos-${item.id}-${carIndexUsed}-tablet`}
                        id={`exbannerpos-${item.id}-${carIndexUsed}`}
                        className={` flex-1 ${styles.exbannerwrap} ${styles.tablet}`}
                    >
                        <ExchangeCarBanner lang={lang} />
                    </div>
                )}
                {carIndexUsed === exchangePositionDesktop && (
                    <div
                        key={`exbannerpos-${item.id}-${carIndexUsed}-desktop`}
                        id={`exbannerpos-${item.id}-${carIndexUsed}`}
                        className={`${styles.exbannerwrap} ${styles.desktop} flex-1`}
                    >
                        <ExchangeCarBanner lang={lang} />
                    </div>
                )}
                {carIndexUsed === financePositionAll && <FinanceCarCard />}
                {carIndexUsed === valuePropositionDesktop && (
                    <div
                        key={`ValuePropositionSlider-${carIndexUsed}-desktop`}
                        className={`${styles.value_proposition} ${styles.desktop} `}
                    >
                        <ValuePropositionDesktop lang={lang} />
                    </div>
                )}
                {carIndexUsed == valuePropositionMobile && (
                    <div
                        key={`ValuePropositionSlider-${carIndexUsed}-mobile`}
                        className={`${styles.value_proposition} ${styles.mobile}`}
                    >
                        <ValuePropositionMobile lang={lang} />
                    </div>
                )}
                {carIndexUsed == valuePropositionTablet && (
                    <div
                        key={`ValuePropositionSlider-${carIndexUsed}-tablet`}
                        className={`${styles.value_proposition} ${styles.tablet}`}
                    >
                        <ValuePropositionMobile lang={lang} />
                    </div>
                )}
                {carIndexUsed <= 29 && carIndexUsed === reserveSeeDesktop && (
                    <div
                        className={
                            'max-md:hidden  flex-1 md:col-span-2 xl:col-span-3 h-full'
                        }
                    >
                        {/*<ReserveSeeCarBanner variant={'card'} />*/}
                        <PurchaseCarStepsBanner
                            key={`ReserveSeeCarBanner-${carIndexUsed}-card`}
                        />
                    </div>
                )}
                {
                carIndexUsed === reserveSeeDesktop && resultsCount.current > 1 && (
                    <div
                        key={`exbannerpos-${item.id}-${carIndexUsed}-mobile`}
                        id={`exbannerpos-${item.id}-${carIndexUsed}`}
                        className={`${styles.exbannerwrap} ${styles.mobile}`}
                    >
                        <ExchangeCarBanner lang={lang} />
                    </div>
                )
                }
            </>
        );
    };

    // renderCards.displayName = 'renderCards';
    // const vehicles = useRef<CarListingData[]>(carsData);

    const [vehicles, setVehicles] = useState(carsData);

    // const [filters, setFilters] = useState<TFilterTerm<CarListingData>[]>(initFilters);

    const { IS_DESKTOP: desktopView = false } = useWindowDimensions(
        DimensionsTargets.MOBILE
    );

    useEffect(() => {
        // load more data.
        if (inView && hasMoreResults && !isPending && !pagePending) {
            // setLoading(true);
            startTransition(() => {
                getCarListingsElastic({
                    // from: lastItem.current,
                    size: vehicles.length + ELASTIC_SEARCH_PAGE_SIZE + 20,
                    filters: convertClientToEsFilters(
                        filters.current,
                        searchTerms
                    ),
                    sortingPlan: sortingOptions,
                })
                    .then(newCars => {
                        let newSize = vehicles.length;
                        if (newCars.data.length) {
                            // lastItem.current += newCars.data.length;
                            // newSize += newCars.data.length;
                            // newVehiclesList = [...vehicles, ...newCars.data];
                            // vehicles.current = newCars.data;
                            newSize = newCars.data.length;
                            setVehicles(newCars.data);
                        }

                        setHasMoreResults(newSize < resultsCount.current);
                    })
                    .catch(err => {
                        // eslint-disable-next-line no-console
                        console.log(err);
                    });
            });
        }
    }, [inView]);

    const doSearch = useDebounce((isFilter = false) => {
        setPagePending(true);
        // if (scrollToTopVisible) {
        window.scrollTo({
            top: 0,
            left: 0,
            behavior: 'auto',
        });
        // }
        startTransition(() => {
            window.history.replaceState(
                {},
                '',
                new URL(
                    `${lang}${buildUrl(filters.current)}`,
                    window.location.origin
                )
            );
            getCarListingsElastic({
                filters: convertClientToEsFilters(filters.current, searchTerms),
                sortingPlan: sortingOptions,
            })
                .then(newCars => {
                    resultsCount.current = newCars.total;
                    // lastItem.current = newCars.data.length;
                    // const newVehiclesList = newCars.data;
                    // vehicles.current = newVehiclesList;
                    setVehicles(newCars.data);
                    setHasMoreResults(
                        newCars.data.length === ELASTIC_SEARCH_PAGE_SIZE
                    );
                    setPagePending(false);

                    if (isFilter) {
                        prevFilters.current = cloneClientRetailFilters(
                            filters.current
                        );
                        trackEvent(
                            'Action: Apply Filter',
                            buildTrackingObject(
                                filters.current,
                                resultsCount.current
                            )
                        );
                    }
                })
                .catch(err => {
                    // eslint-disable-next-line no-console
                    console.log(err);
                    setPagePending(false);
                });
        });
    }, 500);

    // _______________________________________________________

    return (
        <>
            <ScrollToTopButton />
            <div
                className={
                    ' flex justify-center flex-col lg:flex-row lg:justify-between relative'
                }
            >
                <div
                    className={
                        ' hidden lg:block w-1/4 h-fit top-0 en:pr-4 ar:pl-4 '
                    }
                >
                    {desktopView && (
                        <Filters
                            searchTerms={searchTerms}
                            filters={filters.current}
                            setFilters={setFilters}
                            displayedCount={resultsCount.current}
                            lang={lang}
                        />
                    )}
                </div>
                <div
                    className={
                        'block lg:hidden overflow-hidden relative mb-2 aspect-[389/199]'
                    }
                >
                    <RetailBanners
                        bannersData={bannersData}
                        showArrows={false}
                        autoPlay={true}
                        width={300}
                        height={190}
                        isRtl={IS_RTL}
                        isDesktop={false}
                    />
                </div>
                <div
                    className={
                        'block lg:hidden z-[1000] pt-1 top-0 min-h-[44px] sticky bg-white pb-3 '
                    }
                >
                    {!desktopView && (
                        <FilterHeaderMobile
                            searchTerms={searchTerms}
                            filters={filters.current}
                            setFilters={setFilters}
                            displayedCount={resultsCount.current}
                            lang={lang}
                        />
                    )}
                </div>
                <div className={'flex flex-col gap-2 lg:gap-6 lg:w-3/4'}>
                    <div
                        className={`${styles.banners_container} hidden lg:block `}
                    >
                        <RetailBanners
                            autoPlay={true}
                            showArrows={true}
                            bannersData={bannersData}
                            width={1294}
                            height={203}
                            isRtl={IS_RTL}
                            isDesktop={true}
                        />
                    </div>
                    <div
                        className={`${styles.sort_desktop_container} min-h-14 hidden lg:block`}
                    >
                        {desktopView && (
                            <Sort filteredCount={resultsCount.current} />
                        )}
                    </div>
                    {pagePending ? (
                        <Spinner
                            style={{
                                minHeight: '200px',
                                alignItems: 'flex-start',
                            }}
                        />
                    ) : (
                        <>
                            <Typography
                                className={'block lg:hidden'}
                                color={colors.gray}
                                variant={'body1Bold'}
                            >
                                {`${resultsCount.current} ${t(
                                    'Sylndr certified cars sort'
                                )}`}
                            </Typography>

                            <div
                                //${styles.cars_listing_container}
                                className={
                                    'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-y-4 gap-x-2 w-full '
                                }
                            >
                                {vehicles.map((item, carIndexUsed) => (
                                    <RenderCards
                                        key={`g-car-${item.id}-${
                                            item.status
                                        }-${carIndexUsed}-${item.updatedAt}-${
                                            item.isNewlyAdded ? 'new' : 'old'
                                        }`}
                                        item={item}
                                        carIndexUsed={carIndexUsed}
                                    />
                                ))}
                                {!hasMoreResults && (
                                    <div className={'flex-1 min-h-[380px]'}>
                                        <CantFindCarCard />
                                    </div>
                                )}
                            </div>
                            {isPending && hasMoreResults && (
                                <Spinner style={{ minHeight: '100px' }} />
                            )}
                            {!isPending && hasMoreResults && (
                                <div
                                    className={styles.hiddenloadingare}
                                    ref={loaderRef}
                                >
                                    {' '}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
            {!hasMoreResults && (
                <div className={styles.testamonialContainer}>
                    <div></div>
                    <div
                        className={`${styles.testamonials} container lg:w-3/4 w-full `}
                    >
                        <VidTestimonialsSection
                            SourceScreen={SourceScreen.CAR_LISTING}
                            serviceName={ServiceName.RETAIL}
                        />
                    </div>
                </div>
            )}
        </>
    );
};
export default CarsListing;
